/**
 * svgScratch — outillage SVG partagé du dossier pensée informatique (5e, thème #8).
 *
 * Visuels STATIQUES générés par construction (jamais d'images figées) :
 *   - piles de blocs façon Scratch (couleurs canoniques par famille de blocs,
 *     chapeau « quand le drapeau est cliqué », cadre « répéter » en C) —
 *     généralisation du précédent gen_940 (script 3 blocs en dur) ;
 *   - expressions par blocs imbriqués (ovales verts d'opérateurs, ovale bleu
 *     « réponse », ovale orange « ma variable », pastilles blanches pour les
 *     nombres) — l'imbrication REMPLACE les parenthèses (cadrage O3-O5) ;
 *   - tracés au stylo dans un petit repère (polylignes en pas Scratch) pour
 *     les QCM de figures (Ex.4 O1, schémas Créé n°60, polygones O7).
 *
 * Tout est paramétré par les DONNÉES du tirage (source de vérité unique).
 */

// ─── Emballages payload (idiome svgStats) ────────────────────

/** Élément additionnel d'ÉNONCÉ (imageUrl en data-URI : correct ici, §12).
 *  `alt` (ajout 2026-09-23, revue 193) : texte de remplacement, lu à l'écran
 *  et à l'impression ; ABSENT, la config reste octet pour octet celle d'avant
 *  (aucun autre gen n'en passe). */
export const imgElement = (svg, maxWidth = 'small', alt = null) => ({
    version: 1,
    elements: [{ type: 'image', config: {
        imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth,
        ...(alt ? { alt } : {}),
    } }],
});

/** CHOIX riche de QCM (règle 🔴 §1 : imageData obligatoire en choix).
 *  `alt` (ajout 2026-09-23, revue 193) : lu par l'impression (normalizeQcm) ;
 *  absent, le choix reste octet pour octet celui d'avant. */
export const imgChoix = (svg, alt = null) => ({
    type: 'image',
    config: {
        imageData: `data:image/svg+xml,${encodeURIComponent(svg)}`,
        ...(alt ? { alt } : {}),
    },
});

export const MOINS = '−';   // U+2212 (affichage uniquement, jamais en saisie)

// ─── Palette Scratch (familles de blocs) ─────────────────────

export const COULEURS_BLOCS = {
    hat: '#d9a404',        // événements (chapeau)
    move: '#3373cc',       // mouvement
    looks: '#774dcb',      // apparence (dire, penser)
    sensing: '#2e8eb8',    // capteurs (demander, réponse)
    pen: '#0b8e69',        // stylo
    control: '#cf8b17',    // contrôle (répéter, attendre)
    variable: '#db6e00',   // variables (mettre ma variable à)
    sound: '#a63fa6',      // son (jouer le son…)
    op: '#389438',         // opérateurs
};

const FS = 13;                          // taille de police des blocs
const largeurTexte = (txt) => Math.ceil(String(txt).length * 6.9) + 4;

// ─── Piles de blocs ──────────────────────────────────────────
//
// Un script = liste de blocs :
//   { t: 'hat'|'move'|'looks'|'sensing'|'pen'|'control'|'variable', txt }
//   { t: 'repeat', n, body: [blocs] }   → cadre en C « répéter n fois »
//     (n: 'indéfiniment' → « répéter indéfiniment » ; jusqua: cond →
//      « répéter jusqu'à ⟨cond⟩ »)
//   { t: 'if', cond, body: [blocs] }    → cadre en C « si ⟨cond⟩ alors »
//   { t: 'ifelse', cond, body, elseBody } → cadre en C à deux branches
//                                           (barre « sinon » médiane) — le NEUF 4e
//
// construireScript → fragment positionné en (0,0) + dimensions, pour
// composer un ou plusieurs scripts dans un même SVG.

const BH = 30;      // hauteur d'un bloc simple
const GAP = 4;      // espacement vertical
const BRAS = 16;    // largeur du bras vertical du cadre répéter
const PIED = 12;    // hauteur du bras de fermeture du cadre répéter

const rect = (x, y, w, h, fill, rx = 6) =>
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${rx}" fill="${fill}" stroke="#0f172a" stroke-opacity="0.25" stroke-width="1"/>`;
const texteBloc = (x, y, txt) =>
    `<text x="${x}" y="${y}" font-size="${FS}" fill="#fff" font-family="Arial">${txt}</text>`;

/** Étiquette (barre haute) d'un cadre en C, selon son type.
 *  Pour 'repeat' : `jusqua` → « répéter jusqu'à ⟨cond⟩ » ; sinon `n` numérique →
 *  « répéter n fois » ; sinon → « répéter <n> » (« répéter indéfiniment » via
 *  n: 'indéfiniment'). */
const etiquetteCadre = (b) => (b.t === 'repeat'
    ? (b.jusqua ? `répéter jusqu'à ${b.jusqua}` : typeof b.n === 'number' ? `répéter ${b.n} fois` : `répéter ${b.n}`)
    : b.t === 'if' || b.t === 'ifelse' ? `si ${b.cond} alors` : '');

/** Largeur nécessaire d'une liste de blocs (récursif, cadres compris). */
const largeurBlocs = (blocs) => Math.max(...blocs.map((b) => {
    if (b.t === 'repeat' || b.t === 'if' || b.t === 'ifelse') {
        const branches = [BRAS + 6 + largeurBlocs(b.body)];
        if (b.t === 'ifelse') branches.push(BRAS + 6 + largeurBlocs(b.elseBody), largeurTexte('sinon') + 24);
        return Math.max(largeurTexte(etiquetteCadre(b)) + 24, ...branches);
    }
    return largeurTexte(b.txt) + 24;
}));

const construireBlocs = (blocs, x, y, w) => {
    let frag = '';
    let cy = y;
    blocs.forEach((b) => {
        if (b.t === 'repeat' || b.t === 'if' || b.t === 'ifelse') {
            const wBody = w - BRAS - 6;
            const corps = construireBlocs(b.body, x + BRAS + 6, cy + BH + GAP, wBody);
            // branche « sinon » éventuelle : barre médiane + corps indenté
            let apres = cy + BH + GAP + corps.h + GAP;   // y sous la 1ʳᵉ branche
            let sinonFrag = '';
            if (b.t === 'ifelse') {
                const els = construireBlocs(b.elseBody, x + BRAS + 6, apres + BH + GAP, wBody);
                sinonFrag = rect(x, apres, w, BH, COULEURS_BLOCS.control)
                    + texteBloc(x + 10, apres + BH / 2 + 5, 'sinon')
                    + els.frag;
                apres = apres + BH + GAP + els.h + GAP;
            }
            const hCadre = apres + PIED - cy;
            // fond du cadre (barre haute + bras gauche continu + pied), en trois rects
            frag += rect(x, cy, w, BH, COULEURS_BLOCS.control)
                + rect(x, cy + BH, BRAS, hCadre - BH, COULEURS_BLOCS.control, 4)
                + rect(x, cy + hCadre - PIED, w * 0.72, PIED, COULEURS_BLOCS.control, 4)
                + texteBloc(x + 10, cy + BH / 2 + 5, etiquetteCadre(b))
                + (b.t === 'repeat' ? `<text x="${x + w * 0.72 - 16}" y="${cy + hCadre - 2}" font-size="12" fill="#fff" font-family="Arial">⟲</text>` : '')
                + corps.frag + sinonFrag;
            cy += hCadre + GAP;
        } else {
            const estChapeau = b.t === 'hat';
            frag += (estChapeau
                ? `<path d="M ${x} ${cy + BH} v ${-(BH - 12)} q 0 -12 14 -12 h 28 q 10 -9 20 0 h ${w - 76} q 14 0 14 12 v ${BH - 12} z" fill="${COULEURS_BLOCS.hat}" stroke="#0f172a" stroke-opacity="0.25" stroke-width="1"/>`
                : rect(x, cy, w, BH, COULEURS_BLOCS[b.t] || COULEURS_BLOCS.move))
                + texteBloc(x + 10, cy + BH / 2 + 5, b.txt);
            cy += BH + GAP;
        }
    });
    return { frag, h: cy - y - GAP };
};

/** Un script seul → SVG autonome. `opts.desc` : chaîne machine relue par le
 *  solveur (structure du script, ex. « si-dans-boucle ») — additif. */
export const svgScript = (blocs, opts = {}) => {
    const w = largeurBlocs(blocs);
    const { frag, h } = construireBlocs(blocs, 4, 4, w);
    const W = w + 8, H = h + 8;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`
        + (opts.desc ? `<desc>${opts.desc}</desc>` : '')
        + `<rect x="0" y="0" width="${W}" height="${H}" fill="#eef2f7" rx="8"/>${frag}</svg>`;
};

/**
 * Plusieurs scripts dessinés dans le MÊME cadre (ajout 2026-09-23, revue 193),
 * pour les choix d'un QCM d'images : blocs de même largeur, même viewBox pour
 * tous, fond identique. L'app borne la HAUTEUR d'une image de choix
 * (ChoixImageEleve, max-h-28 / max-h-36) : sans cadre commun, un script plus
 * court s'afficherait en plus gros que les autres et se distinguerait par sa
 * taille, pas par son contenu. Un script plus court que le plus long garde un
 * espace vide en bas. Marge haute de 8 et non 4 : la bosse du chapeau monte à
 * 4,5 au-dessus du bloc, elle sortait d'un demi-pixel du viewBox. Retourne un
 * SVG autonome par liste de blocs, dans l'ordre.
 */
export const svgScriptsMemeCadre = (listeBlocs) => {
    const w = Math.max(...listeBlocs.map((blocs) => largeurBlocs(blocs)));
    const corps = listeBlocs.map((blocs) => construireBlocs(blocs, 4, 8, w));
    const W = w + 8;
    const H = Math.max(...corps.map((c) => c.h)) + 12;
    return corps.map(({ frag }) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`
        + `<rect x="0" y="0" width="${W}" height="${H}" fill="#eef2f7" rx="8"/>${frag}</svg>`);
};

/**
 * Texte de remplacement d'un script (ajout 2026-09-23, revue 193) : les blocs
 * dans l'ordre, séparés par des virgules, le contenu d'un cadre « répéter » ou
 * « si … alors » entre crochets. Mêmes libellés que le dessin (etiquetteCadre),
 * donc rien de plus que ce que l'image montre.
 */
export const altScript = (blocs) => {
    const lire = (liste) => liste.map((b) => {
        if (b.t === 'repeat' || b.t === 'if') return `${etiquetteCadre(b)} [${lire(b.body)}]`;
        if (b.t === 'ifelse') return `${etiquetteCadre(b)} [${lire(b.body)}] sinon [${lire(b.elseBody)}]`;
        return b.txt;
    }).join(', ');
    return `Script : ${lire(blocs)}.`;
};

/** Plusieurs scripts côte à côte, chacun avec un titre (« Programme A »…). */
export const svgScripts = (scripts) => {
    const mT = 22;
    const colonnes = scripts.map(({ titre, blocs }) => {
        const w = Math.max(largeurBlocs(blocs), largeurTexte(titre || ''));
        const corps = construireBlocs(blocs, 0, 0, w);
        return { titre, w, ...corps };
    });
    const H = mT + Math.max(...colonnes.map((c) => c.h)) + 10;
    let x = 8;
    let frag = '';
    colonnes.forEach((c) => {
        if (c.titre) frag += `<text x="${x}" y="14" font-size="13" font-weight="bold" fill="#1e293b" font-family="Arial">${c.titre}</text>`;
        frag += `<g transform="translate(${x},${mT})">${c.frag}</g>`;
        x += c.w + 24;
    });
    const W = x - 16;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`
        + `<rect x="0" y="0" width="${W}" height="${H}" fill="#eef2f7" rx="8"/>${frag}</svg>`;
};

// ─── Expressions par blocs imbriqués ─────────────────────────
//
// AST : nombre (number, virgule FR à l'affichage), 'réponse', 'ma variable',
// ou { op: '+'|'−'|'×'|'/', a, b }. L'imbrication porte les priorités.

const OPS_AFFICHES = { '+': '+', '-': MOINS, '−': MOINS, '*': '×', '×': '×', '/': '/' };

export const fmtFr = (v) => String(v).replace('.', ',').replace('-', MOINS);

const EH = 24;      // hauteur d'une pastille opérande
const PAD = 7;      // padding interne d'un ovale opérateur

const construireExpr = (node) => {
    if (typeof node === 'number' || (typeof node === 'string' && !['réponse', 'ma variable'].includes(node))) {
        const txt = fmtFr(node);
        const w = largeurTexte(txt) + 14;
        return { w, h: EH, frag: (x, y) => rect(x, y, w, EH, '#ffffff', EH / 2)
            + `<text x="${x + w / 2}" y="${y + EH / 2 + 5}" font-size="${FS}" fill="#0f172a" text-anchor="middle" font-family="Arial">${txt}</text>` };
    }
    if (node === 'réponse' || node === 'ma variable') {
        const fill = node === 'réponse' ? COULEURS_BLOCS.sensing : COULEURS_BLOCS.variable;
        const w = largeurTexte(node) + 16;
        return { w, h: EH, frag: (x, y) => rect(x, y, w, EH, fill, EH / 2)
            + `<text x="${x + w / 2}" y="${y + EH / 2 + 5}" font-size="${FS}" fill="#fff" text-anchor="middle" font-family="Arial">${node}</text>` };
    }
    const a = construireExpr(node.a);
    const b = construireExpr(node.b);
    const opTxt = OPS_AFFICHES[node.op] || node.op;
    const wOp = 18;
    const h = Math.max(a.h, b.h) + 2 * PAD;
    const w = PAD + a.w + wOp + b.w + PAD;
    return { w, h, frag: (x, y) => rect(x, y, w, h, COULEURS_BLOCS.op, h / 2)
        + a.frag(x + PAD, y + (h - a.h) / 2)
        + `<text x="${x + PAD + a.w + wOp / 2}" y="${y + h / 2 + 5}" font-size="${FS + 1}" fill="#fff" text-anchor="middle" font-weight="bold" font-family="Arial">${opTxt}</text>`
        + b.frag(x + PAD + a.w + wOp, y + (h - b.h) / 2) };
};

/** Une expression par blocs seule → SVG autonome. */
export const svgExpr = (ast) => {
    const e = construireExpr(ast);
    const W = e.w + 12, H = e.h + 12;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`
        + `<rect x="0" y="0" width="${W}" height="${H}" fill="#eef2f7" rx="8"/>${e.frag(6, 6)}</svg>`;
};

/** Bloc « mettre ma variable à ⟨expr⟩ » (ou autre préfixe) → SVG autonome. */
export const svgBlocExpr = (prefixe, ast, fill = COULEURS_BLOCS.variable) => {
    const e = construireExpr(ast);
    const wPre = largeurTexte(prefixe);
    const h = e.h + 12;
    const W = 10 + wPre + 8 + e.w + 10 + 8;
    const H = h + 8;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`
        + rect(4, 4, W - 8, h, fill, 8)
        + `<text x="${14}" y="${4 + h / 2 + 5}" font-size="${FS}" fill="#fff" font-family="Arial">${prefixe}</text>`
        + e.frag(14 + wPre + 8, 4 + 6)
        + '</svg>';
};

/** Évalue un AST (côté gen uniquement — le solveur re-parse le SVG rendu). */
export const evalExpr = (node) => {
    if (typeof node === 'number') return node;
    if (typeof node === 'object') {
        const a = evalExpr(node.a), b = evalExpr(node.b);
        switch (node.op) {
            case '+': return a + b;
            case '-': case '−': return a - b;
            case '*': case '×': return a * b;
            default: return a / b;
        }
    }
    throw new Error(`evalExpr : opérande non numérique ${node}`);
};

/** Clé structurelle d'un AST (parenthésage complet) — unicité des choix. */
export const cleExpr = (node) => {
    if (typeof node === 'object') return `(${cleExpr(node.a)}${OPS_AFFICHES[node.op] || node.op}${cleExpr(node.b)})`;
    return fmtFr(node);
};

/**
 * Écriture mathématique usuelle d'un AST : parenthèses UNIQUEMENT quand
 * l'imbrication contredit les priorités (l'enjeu didactique du cluster 3).
 */
const PRIO = { '+': 1, '-': 1, '−': 1, '*': 2, '×': 2, '/': 2 };
export const exprEnEcriture = (node, varNom = null) => {
    if (typeof node === 'number') return fmtFr(node);
    if (typeof node === 'string') return varNom || node;
    const gauche = exprEnEcriture(node.a, varNom);
    const droite = exprEnEcriture(node.b, varNom);
    const p = PRIO[node.op];
    const parA = typeof node.a === 'object' && PRIO[node.a.op] < p;
    // droite : parenthèses si priorité inférieure, ou égale avec − et / (non associatifs)
    const parB = typeof node.b === 'object'
        && (PRIO[node.b.op] < p || (PRIO[node.b.op] === p && ['-', '−', '/'].includes(node.op)));
    return `${parA ? `(${gauche})` : gauche} ${OPS_AFFICHES[node.op] || node.op} ${parB ? `(${droite})` : droite}`;
};

// ─── Tracés au stylo (repère en pas Scratch) ─────────────────

/**
 * svgTraces(chemins, opts) → SVG d'un petit repère avec polylignes.
 * `chemins` : [{ points: [[x,y]…] (pas Scratch), couleur, fleche }].
 * `opts.compact` : gabarit choix de QCM. Axes légers par l'origine,
 * point de départ marqué. Échelle auto sur la boîte englobante.
 */
export const svgTraces = (chemins, opts = {}) => {
    const { compact = false } = opts;
    const tous = chemins.flatMap((c) => c.points).concat([[0, 0]]);
    const xs = tous.map((p) => p[0]), ys = tous.map((p) => p[1]);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const cote = compact ? 130 : 190;
    const marge = 16;
    const ech = (cote - 2 * marge) / Math.max(maxX - minX, maxY - minY, 1);
    const px = (x) => marge + (x - minX) * ech;
    const py = (y) => cote - marge - (y - minY) * ech;

    let frag = '';
    // axes par l'origine (repère d'orientation, sans graduations)
    frag += `<line x1="${px(minX)}" y1="${py(0)}" x2="${px(maxX)}" y2="${py(0)}" stroke="#94a3b8" stroke-width="1"/>`
        + `<line x1="${px(0)}" y1="${py(minY)}" x2="${px(0)}" y2="${py(maxY)}" stroke="#94a3b8" stroke-width="1"/>`;
    chemins.forEach((c) => {
        const pts = c.points.map(([x, y]) => `${px(x).toFixed(1)},${py(y).toFixed(1)}`).join(' ');
        frag += `<polyline points="${pts}" fill="none" stroke="${c.couleur || '#1E3A8A'}" stroke-width="2.5" stroke-linejoin="round"/>`;
        const [x0, y0] = c.points[0];
        frag += `<circle cx="${px(x0).toFixed(1)}" cy="${py(y0).toFixed(1)}" r="3.5" fill="${c.couleur || '#1E3A8A'}"/>`;
        if (c.fleche && c.points.length >= 2) {
            const [xa, ya] = c.points[c.points.length - 2];
            const [xb, yb] = c.points[c.points.length - 1];
            const dx = px(xb) - px(xa), dy = py(yb) - py(ya);
            const L = Math.hypot(dx, dy) || 1;
            const ux = dx / L, uy = dy / L;
            const t = 9;
            frag += `<polygon points="${px(xb).toFixed(1)},${py(yb).toFixed(1)} ${(px(xb) - t * ux - t * 0.5 * uy).toFixed(1)},${(py(yb) - t * uy + t * 0.5 * ux).toFixed(1)} ${(px(xb) - t * ux + t * 0.5 * uy).toFixed(1)},${(py(yb) - t * uy - t * 0.5 * ux).toFixed(1)}" fill="${c.couleur || '#1E3A8A'}"/>`;
        }
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${cote} ${cote}" width="${cote}" height="${cote}" font-family="Arial">`
        + `<rect x="0" y="0" width="${cote}" height="${cote}" fill="#f8fafc" stroke="#e2e8f0"/>${frag}</svg>`;
};
